// TituloDeSecao.js — os títulos da loja no desenho dos outros dois sites.
//
// (18/10/2026) O dono pediu a loja inteira no desenho do site do Congresso e
// do site principal. Estas são as mesmas peças de lá (insigne-cida,
// components/ui/Texto.js), com o mesmo nome:
//
//   Ornamento          traço, losango, traço (embaixo de todo título);
//   TituloComDestaque  o título com um trecho em laranja;
//   TituloDeSecao      chapéu, título na League Gothic, ornamento e subtítulo;
//   CabecalhoDePagina  a abertura das páginas (catálogo, autores, FAQ...).
//
// O TRECHO EM LARANJA: `destaque` é um pedaço do próprio título. Sem ele, sai
// em laranja a ÚLTIMA palavra ("Produtos em DESTAQUE"), que é o que os outros
// dois sites fazem quase sempre. `destaque={false}` desliga.
//
// ESPAÇAMENTOS COM COLCHETES (`mt-[0.75rem]`): a loja carrega o
// bootstrap.min.css inteiro, e as classes que ele também declara (`mt-3`,
// `mb-4`…) chegam com !important e valores maiores.
import React from "react";

/**
 * Os círculos tracejados que giram devagar atrás das aberturas, como no site
 * principal (o "pontilhado radial" do mentor). Três anéis, um girando ao
 * contrário. Enfeite: `aria-hidden`, e param para quem pediu menos movimento.
 * O tamanho e a posição vêm de `className` (absolute fica por conta daqui).
 */
export const Giros = ({ className = "" }) => (
  <div className={"pointer-events-none absolute " + className} aria-hidden="true">
    {[100, 72, 46].map((t, i) => (
      <span
        key={t}
        className={
          "absolute rounded-full border-2 border-dashed " +
          (i === 1 ? "border-insigne-orange/30 girar-reverso" : "border-insigne-orange/20 girar-devagar")
        }
        // Posição por `inset`, e não por translate(-50%): o giro usa a
        // propriedade `rotate`, que se aplica ANTES do `transform`, e o
        // translate girava junto (os anéis saíam do centro, rodando em volta).
        style={{ inset: (100 - t) / 2 + "%" }}
      />
    ))}
  </div>
);

export const Ornamento = ({ className = "" }) => (
  <span className={"ornamento " + className} aria-hidden="true">
    <i />
  </span>
);

export function TituloComDestaque({ titulo, destaque }) {
  const texto = String(titulo ?? "");
  let trecho = destaque;
  if (trecho === undefined || trecho === true) {
    const palavras = texto.trim().split(/\s+/);
    trecho = palavras.length > 1 ? palavras[palavras.length - 1] : null;
  }
  if (!trecho || !texto.includes(trecho)) return <>{texto}</>;
  const i = texto.lastIndexOf(trecho);
  return (
    <>
      {texto.slice(0, i)}
      <span className="text-insigne-orange">{trecho}</span>
      {texto.slice(i + trecho.length)}
    </>
  );
}

/**
 * @param chapeu     duas ou três palavras em cima, com o traço laranja
 * @param titulo     o nome da seção
 * @param destaque   o trecho em laranja (padrão: a última palavra)
 * @param subtitulo  uma frase embaixo
 * @param centro     centraliza tudo
 * @param tamanho    classes de tamanho do título
 * @param acao       um nó à direita (o "Ver todos"), alinhado embaixo
 */
export function TituloDeSecao({
  chapeu,
  titulo,
  destaque,
  subtitulo,
  centro = false,
  nivel: Tag = "h2",
  tamanho = "text-[2.4rem] sm:text-[3.1rem]",
  className = "",
  id,
  icone,
  acao,
}) {
  return (
    <div className={"flex items-end justify-between gap-x-6 gap-y-[1rem] " + (centro ? "flex-col " : "flex-wrap ") + className}>
      <div className={"flex min-w-0 flex-col " + (centro ? "w-full items-center text-center" : "items-start")}>
        {chapeu && (
          <span className={"chapeu " + (centro ? "chapeu-centro" : "")}>
            {icone && <i className={"bi " + icone + " -mr-[0.125rem] text-[0.85rem]"} aria-hidden="true" />}
            {chapeu}
          </span>
        )}
        <Tag
          id={id}
          className={
            "titulo-display mt-[0.6rem] mb-0 text-white " + tamanho + (centro ? " max-w-[26ch]" : " max-w-[24ch]")
          }
        >
          <TituloComDestaque titulo={titulo} destaque={destaque} />
        </Tag>
        <Ornamento className="mt-[0.9rem]" />
        {subtitulo && (
          <p className={"mt-[0.9rem] mb-0 max-w-[62ch] text-[0.95rem] leading-relaxed text-white/65 md:text-base " + (centro ? "mx-auto" : "")}>
            {subtitulo}
          </p>
        )}
      </div>
      {acao && <div className="shrink-0">{acao}</div>}
    </div>
  );
}

/**
 * A abertura de uma página: faixa escura com a grade de pontos e a luz
 * laranja, o título grande e, embaixo, o que a página quiser (`children`).
 * `migalhas` vai em cima do chapéu; `lado` fica à direita do título no
 * computador (um número em contorno, um selo, um botão).
 */
export function CabecalhoDePagina({ chapeu, titulo, destaque, lead, migalhas, lado, children, className = "" }) {
  return (
    <section className={"relative isolate overflow-hidden border-b-2 border-insigne-sombra bg-insigne-navy-deep " + className}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="textura-pontos absolute inset-0 [mask-image:linear-gradient(to_left,black,transparent_75%)]" />
        <div className="brilho-laranja -right-40 -top-48 h-[34rem] w-[34rem]" />
        <div className="brilho-azul -left-56 bottom-[-18rem] h-[30rem] w-[30rem]" />
        <Giros className="right-[-6rem] top-1/2 hidden h-[26rem] w-[26rem] -translate-y-1/2 lg:block" />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-[2.5rem] md:px-8 md:py-[3.5rem]">
        {migalhas && <div className="mb-[1.25rem]">{migalhas}</div>}
        <div className="flex flex-col gap-[1.5rem] lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            {chapeu && <span className="chapeu">{chapeu}</span>}
            <h1 className="titulo-display mb-0 mt-[0.75rem] max-w-[20ch] text-[3rem] text-white sm:text-[4.25rem]">
              <TituloComDestaque titulo={titulo} destaque={destaque} />
            </h1>
            <Ornamento className="mt-[1.1rem]" />
            {lead && (
              <p className="mb-0 mt-[1.1rem] max-w-[62ch] text-base leading-relaxed text-white/75 md:text-[1.05rem]">{lead}</p>
            )}
          </div>
          {lado && <div className="shrink-0">{lado}</div>}
        </div>
        {children && <div className="mt-[1.75rem]">{children}</div>}
      </div>
    </section>
  );
}

export default TituloDeSecao;
